import React, { useMemo, useState } from 'react';
import { Modal, Box, Text } from '@mantine/core';
import type { LocationTreeNode } from '../api/schedulingApis';
import { LocationHierarchyTree } from './LocationHierarchyTree';

interface LocationBulkMoveModalProps {
  opened: boolean;
  projectId: string;
  tree: LocationTreeNode[];
  currentParentId: string | null;
  selectedIds: string[];
  onClose: () => void;
  onConfirm: (targetParentId: string | null) => void;
}

export function LocationBulkMoveModal({
  opened,
  projectId,
  tree,
  currentParentId,
  selectedIds,
  onClose,
  onConfirm,
}: LocationBulkMoveModalProps) {
  const [targetParentId, setTargetParentId] = useState<string | null>(null);

  const enabledLocationIds = useMemo<Set<string> | null>(() => {
    if (!tree.length) return null;
    const set = new Set<string>();
    const currentParentStr = currentParentId != null ? String(currentParentId) : null;
    const selectedSet = new Set(selectedIds.map(String));

    function walk(nodes: LocationTreeNode[]) {
      for (const n of nodes) {
        const id = String(n.location_id);
        // Disable choosing the current parent and the selected nodes themselves as a target.
        if (id === currentParentStr || selectedSet.has(id)) {
          // skip
        } else {
          set.add(id);
        }
        if (n.child?.length) walk(n.child);
      }
    }
    walk(tree);
    return set;
  }, [tree, currentParentId, selectedIds]);

  return (
    <Modal opened={opened} onClose={onClose} title="Merge selected locations" size="lg" centered>
      <Box mb="xs">
        <Text size="sm" c="dimmed">
          Choose a new parent for the selected locations. The current parent and the selected
          locations themselves are disabled.
        </Text>
      </Box>
      <Box
        style={{
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 8,
          padding: 4,
          maxHeight: 360,
          overflow: 'hidden',
        }}
      >
        <LocationHierarchyTree
          projectId={projectId}
          dataOverride={tree}
          width="100%"
          height={320}
          disableDrag
          disableEdit
          enabledLocationIds={enabledLocationIds}
          onSelectionChange={(nodes) => {
            const first = nodes[0];
            setTargetParentId(first ? String(first.location_id) : null);
          }}
        />
      </Box>
      <Box mt="md" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" className="location-mgmt-ai-btn" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="location-mgmt-ai-btn location-mgmt-ai-btn--primary"
          disabled={!targetParentId}
          onClick={() => {
            if (!targetParentId) return;
            onConfirm(targetParentId);
          }}
          style={{ opacity: targetParentId ? 1 : 0.5, cursor: targetParentId ? 'pointer' : 'not-allowed' }}
        >
          Merge here
        </button>
      </Box>
    </Modal>
  );
}

